import { useQuery } from '@tanstack/react-query'
import { createVietQRUrl } from '../apis/paymentApi'
import { useContext } from 'react'
import { HotelContext } from '../contexts/HotelContext'
import { Navigate, useLocation } from 'react-router'
import { PaymentMethod } from '../common/constants'
import { object, number, string } from 'yup'
import { getQueryParams, safeCast } from '../utils/urlUtils'
import { Typography, Button, styled } from '@mui/material'
import { Container, Box } from '@mui/system'
import { BackButton } from '../components/BackButton'
import { getHotelReservation } from '../apis/hotelApis'
import { Loader } from '../components/Loader'
import { PaymentStatus } from '../types/hotelTypes'
import { getNextPage } from '../utils/checkinUtils'

export type VietQRTemplate = 'compact' | 'compact2' | 'qr_only' | 'print'

export interface IVietQRRouteParams {
  amount: number
  hotelId: number
  reservationId: number
  paymentMethod: PaymentMethod
  bookingNumber?: number
  template?: VietQRTemplate
}

const schema = object({
  amount: number().required(),
  hotelId: number().required(),
  reservationId: number().required(),
  paymentMethod: string()
    .required()
    .oneOf([PaymentMethod.VietQR_Cake_Email, PaymentMethod.VietQR_Timo_Email, PaymentMethod.VietQR_VietinBank]),
  bookingNumber: number().optional(),
})

export const StyledVietQRPayment = styled(Container)`
  display: flex;
  flex-direction: column;
  align-items: center;

  .img-vietqr {
    width: 20rem;
    height: 20rem;
    border: 4px solid ${({ theme }) => theme.palette.primary.main};
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      max-width: 100%;
      max-height: 100%;
    }
  }
`

export const VietQRPayment = () => {
  const { search } = useLocation()
  const params = getQueryParams(search)
  const parsedParams = safeCast<IVietQRRouteParams>(params, schema)

  const { hotel, reservation: contextReservation } = useContext(HotelContext)
  const { data } = useQuery({
    queryKey: ['createVietQRUrl', search],
    queryFn: () =>
      createVietQRUrl({
        reservationId: contextReservation.id,
        hotelId: hotel.hotelId,
        ...parsedParams,
      } as IVietQRRouteParams),
  })
  const { data: reservation } = useQuery({
    queryKey: [`/api/hotels/${hotel.hotelId}/reservation/${contextReservation.id}`],
    queryFn: () => getHotelReservation(hotel.hotelId, contextReservation.id),
    refetchInterval: 1000,
  })

  const isPaid = reservation?.PaymentStatus === PaymentStatus.Paid
  if (isPaid) {
    return <Navigate to={getNextPage({ ...contextReservation, paymentStatus: PaymentStatus.Paid }).url} />
  }

  return (
    <StyledVietQRPayment maxWidth="sm">
      <BackButton to={`/hotel/${hotel.hotelId}`} />
      <Box py={1}>
        <Typography
          color={(theme) => theme.palette.primary.main}
          mt={1}
          textAlign="center"
          gutterBottom
          variant="h4"
          fontWeight="600">
          Quét mã QR để thanh toán
        </Typography>
      </Box>
      <Box className="img-vietqr">
        {data ? (
          <img className="img-fluid" src={`data:${data.imageMimeType || 'image/png'};base64,${data.imageBase64}`} />
        ) : (
          <Loader />
        )}
      </Box>
      <Box mt={4} width="100%">
        <Button fullWidth variant="contained" type="submit">
          Lưu ý: Vui lòng không thay đổi nội dung chuyển khoản
        </Button>
        <Typography mt={1} align="center">
          Hệ thống sẽ tự động tiếp tục sau khi thanh toán thành công
        </Typography>
      </Box>
    </StyledVietQRPayment>
  )
}
